// eslint-disable-next-line @eslint-community/eslint-comments/disable-enable-pair
/* eslint-disable camelcase */
import { useCallback, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import classnames from 'classnames/bind';
import { Header, THeaderVariant } from '@portals/external-services-header';
import { TColorType } from '@portals/ui-kit/lib/chips/types';
import { SelectOptionType } from '@portals/ui-kit';
import { currentBrandByHost, currentZone } from '@portal-root/geo-utils';
import {
  EBrands,
  TLink,
  TSelectOption,
} from '@portals/external-services-header/lib/_components/header/_types';
import { navigateHandle } from '@/_utils/navigate-handle';
import { useAnalytics } from '@/_hooks/use-analytics';
import { EAnalytics, ELoginButtonPagePosition } from '@/_constants/analytics';
import { redirect } from '@/_utils/redirect';
import { EColors, ECountries } from '@/_types';
import { useWindowSize } from '@/_hooks/use-window-size';
import { COUNTRY_SELECT_VALUES } from '@/_constants/general';
import { Logo } from '../_components/logo';
import { getUrlParams } from '../_utils/get-url-locale-params';
import { EHeaderModifier, THeaderConfig } from '../_types';
import { getActionButtons } from '../_utils/get-action-buttons';
import { isCompactDesktop } from '../_utils/is-compact-desktop';
import styles from './index.module.scss';

const cn = classnames.bind(styles);
const BLOCK_NAME = 'HeaderDesktop';

type TProps = {
  modifier: EHeaderModifier;
  handleChangeCountry: (option: SelectOptionType) => void;
  handleChangeLanguage: (option: SelectOptionType) => void;
};

export const HeaderDesktop = ({
  modifier,
  handleChangeCountry,
  handleChangeLanguage,
  languages,
  countries,
  headerLinks,
  headerPopupLinks,
  loginButton,
  registerButton,
}: TProps & THeaderConfig) => {
  const translations = useTranslations();
  const headerRef = useRef<HTMLDivElement | null>(null);
  const sendEvent = useAnalytics();
  const { country, locale } = getUrlParams();
  const { width } = useWindowSize();

  const isTransparent = modifier === EHeaderModifier.TRANSPARENT;
  const isCustomLogo = currentBrandByHost === EBrands.WB;

  const loginButtonColor: TColorType | undefined = isTransparent
    ? loginButton.color
    : 'primary';
  const registerButtonColor: TColorType | undefined = isTransparent
    ? registerButton.color
    : 'secondary';

  const isTurkey = currentZone.endsWith(ECountries.TR);
  const variant: THeaderVariant =
    isTransparent && !isTurkey ? 'transparent' : 'default';
  const logoColor = isTransparent ? EColors.WHITE : EColors.BLACK;

  const formattedHeaderLinks = headerLinks.map(
    ({ id, title: text, routeName }) => ({
      id,
      text: translations(text),
      routeName,
    }),
  );

  const headerMenuOptions = headerPopupLinks.map(
    ({ id, title, routeName }) => ({
      id,
      title: translations(title),
      routeName,
    }),
  );

  const selectedLanguage = useMemo(
    () => languages?.find(({ id }) => locale === id),
    [languages, locale],
  );

  const selectedCountry = useMemo(
    () => countries.find(({ id }) => country === id),
    [countries, country],
  );

  const formattedCountries = useMemo(() => {
    return countries.map((item) => {
      const color = isTransparent ? EColors.WHITE : EColors.BLACK;
      const DropdownIcon =
        typeof item.icon === 'string' ? (
          item.icon
        ) : (
          <item.icon color={EColors.BLACK} />
        );
      const FlagIcon =
        typeof item.icon === 'string' ? item.icon : <item.icon color={color} />;

      if (isCompactDesktop(width) && item.id === ECountries.RU) {
        return {
          ...item,
          icon: FlagIcon,
          dropdownIcon: DropdownIcon,
          selectTitle: COUNTRY_SELECT_VALUES.ruShort,
        };
      }

      return { ...item, icon: FlagIcon, dropdownIcon: DropdownIcon };
    });
  }, [countries, isTransparent, width]);

  const handleRedirect = useCallback(
    (position: ELoginButtonPagePosition) => {
      sendEvent({
        name: EAnalytics.SUPPLIER_LANDING_AUTH_BUTTON_T,

        params: { position_button: position },
      });
      redirect();
    },
    [sendEvent],
  );

  const handleLogin = useCallback(
    () => handleRedirect(ELoginButtonPagePosition.LOGIN),
    [handleRedirect],
  );

  const handleRegister = useCallback(
    () => handleRedirect(ELoginButtonPagePosition.UP_NEAR_LOGIN),
    [handleRedirect],
  );

  const handleLinkClick = (link: TLink): void => {
    sendEvent({
      name: EAnalytics.SUPPLIER_LANDING_HEADER_ELEMENT_T,
      params: {
        language: locale,
        block: link.id,
      },
    });
    if (link.routeName.startsWith('#')) {
      return navigateHandle({ routeName: link.routeName });
    }

    window.open(link.routeName, '_blank');
  };

  const handleMenuItemClick = (item: TSelectOption): void => {
    sendEvent({
      name: EAnalytics.SUPPLIER_LANDING_MENU_SERVICES_T,
      params: {
        language: locale,
        button_name: item.id,
      },
    });

    if (item.routeName?.startsWith('#')) {
      return navigateHandle({ routeName: item.routeName });
    }

    window.open(item.routeName, '_self');
  };

  const handleOpenMenu = (isMenuOpen: boolean): void => {
    if (isMenuOpen) {
      sendEvent({
        name: EAnalytics.SUPPLIER_LANDING_MENU_V,
        params: {
          language: locale,
        },
      });
    }
  };

  return (
    <div
      ref={headerRef}
      className={cn(BLOCK_NAME, `${BLOCK_NAME}--${modifier}`)}
    >
      <Header
        actionButtons={{
          firstButton: {
            text: translations(loginButton.text),
            onClick: handleLogin,
            ...getActionButtons({
              isTransparent,
              color: loginButtonColor,
              colorHex: loginButton.colorHex,
              textHex: loginButton.textHex,
            }),
          },
          secondButton: {
            text: translations(registerButton.text),
            ...getActionButtons({
              isTransparent,
              color: registerButtonColor,
              colorHex: registerButton.colorHex,
              textHex: registerButton.textHex,
            }),
            onClick: handleRegister,
          },
        }}
        countrySelectParams={
          formattedCountries.length
            ? {
                options: formattedCountries ?? [],
                value: selectedCountry?.id ?? '',
                onChange: handleChangeCountry,
              }
            : undefined
        }
        createInstance
        customLogo={isCustomLogo ? <Logo logoColor={logoColor} /> : undefined}
        languageSelectParams={
          !!languages?.length
            ? {
                options: languages,
                onChange: handleChangeLanguage,
                value: selectedLanguage?.id,
              }
            : undefined
        }
        links={{
          onClick: handleLinkClick,
          desktop: formattedHeaderLinks,
        }}
        menuParams={
          headerMenuOptions.length
            ? {
                desktop: {
                  onOpenChange: handleOpenMenu,
                  onClick: handleMenuItemClick,
                  options: headerMenuOptions,
                },
              }
            : undefined
        }
        variant={variant}
      />
    </div>
  );
};
